import { escape } from 'lodash';
import { Citation } from 'types';

function escapeForHtmlAttribute(text: string): string {
  return escape(text).replace(/\n/g, '<br/>').replace(/\r/g, '<br/>');
}

function citationToLink(citation: Citation): string {
  return `<AICitationLink sourceId="${citation.reference.id}" sourceType="${citation.reference.type}" title="${
    citation.reference.title
  }" quotedText="${escapeForHtmlAttribute(citation.quotedText?.text ?? '')}" quotedPage="${
    citation.quotedText?.page
  }" />`;
}

// We currently support two types of citations:
// 1. Open AI built-in citations support only link to file without a location
// 2. Our own implementation of citations that include specific location and quoted text
// For the first type, openAI can return two adjacent citations to the same file, which has no value to the user.
// This function merges adjacent citations to the same file and creates a merged placeholder, only for the openAI built-in citations.
function mergeAdjacentCitations(citations: Citation[]) {
  // Our own implementation of citations have endIndex and startIndex set to -1. This will never be the case for openAI built-in citations.
  const isOpenAICitation = (citation: Citation) => citation.endIndex !== -1 && citation.startIndex !== -1;

  return citations
    .toSorted((c1, c2) => (c1.startIndex > c2.startIndex ? 1 : -1))
    .reduce((acc, citation) => {
      const prevCitation = acc[acc.length - 1];

      if (
        prevCitation &&
        isOpenAICitation(prevCitation) &&
        prevCitation.reference.id === citation.reference.id &&
        prevCitation.endIndex === citation.startIndex
      ) {
        prevCitation.endIndex = citation.endIndex;
        prevCitation.placeholder += citation.placeholder;
      } else {
        acc.push({ ...citation });
      }
      return acc;
    }, [] as Citation[]);
}

export function stripAllAnnotations(message: string): string {
  return message.replace(/(?:【.*?】|⌈.*?⌉)/g, '');
}

export function resolveCitations(message: string, citations: Citation[]): string {
  const mergedCitations = mergeAdjacentCitations(citations);

  const resolvedCitations = mergedCitations.reduce(
    (acc, citation) => acc.replace(citation.placeholder, citationToLink(citation)),
    message,
  );

  // Remove remaining citations that could not be resolved.
  // This can happen if we failed to create the Citations on the server, but the AI already inserted the placeholders.
  return stripAllAnnotations(resolvedCitations);
}

export function removeCitations(message: string, citations: Citation[]): string {
  return citations.reduce((acc, citation) => acc.replace(citation.placeholder, ''), message);
}
